import {Component, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import {lastValueFrom} from "rxjs";
import {PerformaModulesService} from "src/app/services/performa-modules.service";
import {
    PagingSearchDto,
    GroupManageDto,
    GroupSearchResultDto,
    ModuleDto,
    PerFormaAPIService
} from "src/app/services/pfapi.service";
import Utils from "src/utils";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-group",
    templateUrl: "./group.component.html",
    styleUrl: "./group.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GroupComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private readonly message: NzMessageService,
        private readonly ModulesService: PerformaModulesService
    ) {}

    modules: ModuleDto[] = [];

    groups: GroupSearchResultDto = {
        Data: [],
        Total: 0
    } as GroupSearchResultDto;

    loading = true;
    total = 0;
    pageSizeOptions = [10, 15, 20, 50];
    pageSize = 15;
    pageIndex = 1;

    private lastParams: NzTableQueryParams | null = null;

    // start out with a default sort order so
    // that the column header shows the correct state
    defaultSort: SortDto[] = [{key: "GroupName", value: "ascend"}];
    sort: SortDto[] = [...this.defaultSort];

    searchField = "GroupName";
    searchValue = "";
    searchFields = [{label: "Group Name", value: "GroupName"}];

    columns: {
        title: string;
        compare: ((a: GroupManageDto, b: GroupManageDto) => number) | null;
        fieldName: string | null;
        enabled: boolean;
    }[] = [];

    async ngOnInit() {
        // get a list of Performa modules to determine which columns to show
        this.modules = (await this.ModulesService.Modules()) ?? [];
        // now that we have the modules, we can set up the columns
        this.setupColumns();
    }

    setupColumns() {
        this.columns = [
            {
                title: "Group Name",
                compare: (a: GroupManageDto, b: GroupManageDto) => a.GroupName.localeCompare(b.GroupName),
                fieldName: "GroupName",
                enabled: true
            },
            {
                title: "Users",
                compare: (a: GroupManageDto, b: GroupManageDto) => a.Users - b.Users,
                fieldName: "Users",
                enabled: true
            },
            {
                title: "Admin",
                compare: null,
                fieldName: null,
                enabled: true
            },
            {
                title: "Scan Admin",
                compare: null,
                fieldName: null,
                enabled: this.moduleIsEnabled("Scan")
            },
            {
                title: "AP Check",
                compare: null,
                fieldName: null,
                enabled: this.moduleIsEnabled("APCheck")
            },
            {
                title: "AR Reports",
                compare: null,
                fieldName: null,
                enabled: this.moduleIsEnabled("ARReport")
            },
            {
                title: "FIN Reports",
                compare: null,
                fieldName: null,
                enabled: this.moduleIsEnabled("FINReport")
            },
            {
                title: "PTP Admin",
                compare: null,
                fieldName: null,
                enabled: this.moduleIsEnabled("PTP")
            },
            {
                title: "Edit Group",
                compare: null,
                fieldName: null,
                enabled: true
            }
        ];
    }

    GetData = async () => {
        const params: PagingSearchDto = {
            PageIndex: this.pageIndex,
            PageSize: this.pageSize,
            SearchField: this.searchField || null,
            SearchValue: this.searchValue || null
        };

        const sort = this.sort.filter(s => s.value !== null) || [...this.defaultSort];

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value;
        }
        try {
            this.groups = await lastValueFrom(this.api.postApiConfigureGroupsSearch(params));
            this.total = this.groups.Total;
        } catch (error) {
            this.message.error("Failed to fetch data");
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};

        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        // reset the page index if the sort changes
        if (this.SortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        this.sort = [...params.sort];
        await this.GetData();
    }

    // nz-table likes to fire extraneous onQueryParamsChange events
    // so we need to filter out the ones that don't actually change anything
    private isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value)
        );
    }

    SortChanged = (sort: SortDto[]): boolean => {
        // Filter out non-active sorts (where value is null)
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        // If both are empty, no sort change
        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }

        // If lengths differ, there's a change (e.g., one has a sort, the other doesn't)
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];

        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    moduleIsEnabled(moduleName: string): boolean {
        return this.modules.some(m => m.Name === moduleName && m.IsEnabled);
    }

    getSortOrder(fieldName: string): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }

    async onSearch(): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    async clearSearch(): Promise<void> {
        this.searchField = "GroupName";
        this.searchValue = "";
        this.pageIndex = 1;
        this.sort = [...this.defaultSort];
        await this.GetData();
    }

    onExport = async () => {
        this.loading = true;

        try {
            const blob = await lastValueFrom(
                this.api.getApiConfigureGroupsExport({
                    observe: "response"
                })
            );

            Utils.downloadFileContent(blob, true);
        } catch (e) {
            this.message.error("something went wrong");
            console.log(e);
        } finally {
            this.loading = false;
        }
    };
}
